/**
 * Cross-browser deferral helpers for non-critical work.
 *
 * Both helpers exist so callers don't recreate them inline (the same
 * `requestIdleCallback`-with-`setTimeout`-fallback was previously
 * duplicated in `main.tsx` and `CheckoutContent.tsx`). Centralised here
 * so we can tune the fallback timing in one place if Safari's RIC
 * support changes.
 */

type IdleCallback = () => void;

/**
 * Run `cb` once the browser is idle.
 *
 * Uses `requestIdleCallback` with a 2-second timeout so the callback
 * also fires on busy pages within a bounded delay. Safari ≤16 doesn't
 * implement RIC — we fall back to a short `setTimeout` (default 0ms)
 * which yields to first paint without the RIC heuristics.
 */
export const deferIdle = (cb: IdleCallback, fallbackDelayMs: number = 0): void => {
  // `requestIdleCallback` is typed as required on `Window` by the
  // current lib.dom.d.ts, but it doesn't exist on Safari ≤16. Bridge
  // via a structural check rather than a typed cast.
  const ric: typeof window.requestIdleCallback | undefined = (window as unknown as Record<string, unknown>)
    .requestIdleCallback as typeof window.requestIdleCallback | undefined;
  if (typeof ric === 'function') {
    ric(cb, { timeout: 2000 });
  } else {
    setTimeout(cb, fallbackDelayMs);
  }
};

/**
 * Run `cb` the first time the user engages with the page, or after
 * `timeoutMs` — whichever comes first.
 *
 * Used to defer heavy-but-eventually-required ancillary work (e.g.
 * Forter, which loads a 28KB script + cross-origin XHRs) past first
 * paint without sacrificing functionality. The hard timeout guarantees
 * the callback runs even for users who hit "Pay" with zero prior
 * interaction.
 *
 * Listens for genuine user-engagement signals only: `pointerdown`,
 * `touchstart`, `keydown`. **`scroll` is deliberately excluded** — it
 * fires on any programmatic scroll (input focus, `scrollIntoView`,
 * Stripe ECE iframe layout, React refs auto-focusing), which would
 * defeat the deferral. The hard timeout already covers users who pay
 * without scrolling, clicking, or typing (rare).
 */
export const deferUntilInteraction = (cb: IdleCallback, timeoutMs: number = 3000): void => {
  let fired = false;
  const events: Array<keyof WindowEventMap> = ['pointerdown', 'touchstart', 'keydown'];

  const run = () => {
    if (fired) return;
    fired = true;
    events.forEach((ev) => window.removeEventListener(ev, run as EventListener, true));
    clearTimeout(timer);
    cb();
  };

  events.forEach((ev) =>
    window.addEventListener(ev, run as EventListener, { capture: true, once: true, passive: true })
  );
  const timer = setTimeout(() => run(), timeoutMs);
};
